<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import DealsKanbanColumn from './DealsKanbanColumn.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  pipeline: { type: Object, required: true },
  deals: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
  selectable: { type: Boolean, default: false },
  selectedDealIds: { type: Array, default: () => [] },
  deletable: { type: Boolean, default: false },
});

const emit = defineEmits([
  'dealClick',
  'moveDeal',
  'createInStage',
  'toggleSelect',
  'toggleStage',
  'deleteDeal',
]);

const { t } = useI18n();

const isDragging = ref(false);
const hoveredZone = ref(null);

const STAGE_TYPE_RANK = { regular: 0, won: 1, lost: 2 };

const sortedStages = computed(() =>
  [...(props.pipeline.stages || [])].sort(
    (a, b) =>
      STAGE_TYPE_RANK[a.stageType] - STAGE_TYPE_RANK[b.stageType] ||
      a.position - b.position
  )
);

const dealsByStage = computed(() => {
  const map = {};
  sortedStages.value.forEach(stage => {
    map[stage.id] = props.deals.filter(d => d.pipelineStageId === stage.id);
  });
  return map;
});

const dropZones = computed(() => {
  const wonStage = sortedStages.value.find(s => s.stageType === 'won');
  const lostStage = sortedStages.value.find(s => s.stageType === 'lost');
  const zones = [];
  if (props.deletable) {
    zones.push({
      key: 'delete',
      label: t('DEALS.KANBAN.DROP_DELETE'),
      icon: 'i-lucide-trash-2',
      tone: 'ruby',
    });
  }
  if (lostStage) {
    zones.push({
      key: 'lost',
      label: lostStage.name,
      icon: 'i-lucide-circle-x',
      tone: 'slate',
      stageId: lostStage.id,
    });
  }
  if (wonStage) {
    zones.push({
      key: 'won',
      label: wonStage.name,
      icon: 'i-lucide-trophy',
      tone: 'teal',
      stageId: wonStage.id,
    });
  }
  return zones;
});

const TONE_CLASSES = {
  ruby: {
    idle: 'border-n-ruby-6 text-n-ruby-11 bg-n-ruby-2',
    active: 'border-n-ruby-9 text-n-ruby-11 bg-n-ruby-4',
  },
  slate: {
    idle: 'border-n-slate-6 text-n-slate-11 bg-n-slate-2',
    active: 'border-n-slate-9 text-n-slate-12 bg-n-slate-4',
  },
  teal: {
    idle: 'border-n-teal-6 text-n-teal-11 bg-n-teal-2',
    active: 'border-n-teal-9 text-n-teal-11 bg-n-teal-4',
  },
};

const zoneClass = zone =>
  TONE_CLASSES[zone.tone][hoveredZone.value === zone.key ? 'active' : 'idle'];

const onDragStart = () => {
  isDragging.value = true;
};

const onDragEnd = () => {
  isDragging.value = false;
  hoveredZone.value = null;
};

const onDrop = ({ stageId, dealId }) => {
  onDragEnd();
  emit('moveDeal', { dealId, stageId });
};

const onZoneDrop = (event, zone) => {
  event.preventDefault();
  const dealId = Number(event.dataTransfer.getData('dealId'));
  onDragEnd();
  if (!dealId) return;
  if (zone.key === 'delete') emit('deleteDeal', dealId);
  else emit('moveDeal', { dealId, stageId: zone.stageId });
};
</script>

<template>
  <div class="flex-1 overflow-x-auto overflow-y-hidden px-6 py-4">
    <div v-if="isLoading" class="flex items-center justify-center h-full">
      <span class="text-n-slate-11 text-base">{{ $t('DEALS.LOADING') }}</span>
    </div>
    <div v-else class="flex gap-4 h-full items-start">
      <DealsKanbanColumn
        v-for="stage in sortedStages"
        :key="stage.id"
        :stage="stage"
        :deals="dealsByStage[stage.id] || []"
        :selectable="selectable"
        :selected-deal-ids="selectedDealIds"
        @deal-click="emit('dealClick', $event)"
        @drop="onDrop"
        @create="emit('createInStage', $event)"
        @toggle-select="emit('toggleSelect', $event)"
        @toggle-stage="emit('toggleStage', $event)"
        @drag-start="onDragStart"
        @drag-end="onDragEnd"
      />
    </div>

    <Teleport to="body">
      <div
        v-if="isDragging && dropZones.length"
        class="fixed bottom-6 right-6 z-50 flex gap-3"
      >
        <div
          v-for="zone in dropZones"
          :key="zone.key"
          class="flex flex-col items-center justify-center gap-1.5 w-36 h-24 px-3 rounded-xl border-2 border-dashed text-sm font-medium text-center transition-colors"
          :class="zoneClass(zone)"
          @dragover.prevent="hoveredZone = zone.key"
          @dragleave="hoveredZone = null"
          @drop="onZoneDrop($event, zone)"
        >
          <Icon :icon="zone.icon" class="size-5 pointer-events-none" />
          <span class="truncate max-w-full pointer-events-none">{{
            zone.label
          }}</span>
        </div>
      </div>
    </Teleport>
  </div>
</template>
